import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../api";
import { BASE_URL } from "../../commonUrls";
import { useApiCall } from "../useApiCall";
import "./WeeklyReviewPage.css";

const REFLECTION_KEY = "lifeos_weekly_reflection";

function getStoredReflection(weekStart) {
  try {
    const raw = localStorage.getItem(`${REFLECTION_KEY}_${weekStart}`);
    return raw ? JSON.parse(raw) : { wentWell: "", challenges: "", nextFocus: "" };
  } catch { return { wentWell: "", challenges: "", nextFocus: "" }; }
}

function saveStoredReflection(weekStart, data) {
  localStorage.setItem(`${REFLECTION_KEY}_${weekStart}`, JSON.stringify(data));
}

export default function WeeklyReviewPage() {
  const { execute, loading, error } = useApiCall();
  const [review, setReview] = useState(null);
  const [reflection, setReflection] = useState({ wentWell: "", challenges: "", nextFocus: "" });
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const saveTimer = useRef(null);

  useEffect(() => {
    execute(() =>
      apiFetch(`${BASE_URL}/review/weekly`).then((r) => (r.ok ? r.json() : null))
    ).then((d) => {
      if (d) {
        setReview(d);
        setReflection(getStoredReflection(d.weekStart));
      }
    });
  }, []);

  const updateReflection = (field, value) => {
    const next = { ...reflection, [field]: value };
    setReflection(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (review?.weekStart) saveStoredReflection(review.weekStart, next);
    }, 600);
  };

  const fetchAiInsight = async () => {
    if (!review) return;
    setAiLoading(true);
    setAiError("");
    const plannerRate = review.plannerCreated > 0
      ? Math.round((review.plannerDone / review.plannerCreated) * 100) : 0;
    const habitRate = review.habitsActive > 0
      ? Math.round((review.habitCheckInsThisWeek / (review.habitsActive * 7)) * 100) : 0;

    const context = `Week ${review.weekStart} to ${review.weekEnd}.
Planner: ${review.plannerDone}/${review.plannerCreated} tasks done (${plannerRate}%), ${review.plannerSkipped} skipped. Completed: ${review.completedTitles?.join(", ") || "none"}.
Habits: ${review.habitCheckInsThisWeek} check-ins across ${review.habitsActive} habits (${habitRate}% consistency). ${review.habitsPerfectWeek} habits had a perfect week.
Habit breakdown: ${review.habitStats?.map(h => `${h.name}: ${h.completedDays}/7`).join(", ") || "none"}.
Tasks: ${review.tasksClosedThisWeek} assigned tasks closed, ${review.tasksStillOpen} still open.
Notes: ${review.notesCreatedThisWeek} notes written. ${review.totalSpentThisWeek ? `Spending: ${review.totalSpentThisWeek}. Top: ${review.topSpendingCategory}.` : ""}
Reflection: What went well: "${reflection.wentWell || "not filled"}". Challenges: "${reflection.challenges || "not filled"}". Next week focus: "${reflection.nextFocus || "not filled"}".`;

    try {
      const res = await apiFetch(`${BASE_URL}/assistant`, {
        method: "POST",
        body: JSON.stringify({
          action: "CHAT",
          input: `Give me a brief, personal weekly review summary in 3-4 sentences. Be specific about the numbers, highlight the biggest win and the biggest gap, and end with one concrete suggestion for next week. Here is my data:\n\n${context}`,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAiInsight(data.message || "");
      } else {
        setAiError("Could not generate insight.");
      }
    } catch {
      setAiError("Could not reach AI.");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return <div className="wr-loading">Loading your weekly review...</div>;
  if (error)   return <div className="wr-error">{error}</div>;
  if (!review) return null;

  const plannerRate = review.plannerCreated > 0
    ? Math.round((review.plannerDone / review.plannerCreated) * 100) : 0;
  const habitRate = review.habitsActive > 0
    ? Math.round((review.habitCheckInsThisWeek / (review.habitsActive * 7)) * 100) : 0;

  return (
    <div className="wr-page">
      <div className="wr-header">
        <div>
          <h2 className="wr-title">Weekly Review</h2>
          <p className="wr-subtitle">{review.weekStart} → {review.weekEnd}</p>
        </div>
        <button className="wr-ai-btn" onClick={fetchAiInsight} disabled={aiLoading}>
          {aiLoading ? "Thinking…" : "✦ AI Summary"}
        </button>
      </div>

      {/* AI insight panel */}
      {(aiInsight || aiError) && (
        <div className={`wr-ai-panel ${aiError ? "wr-ai-error" : ""}`}>
          {aiError || aiInsight}
        </div>
      )}

      <div className="wr-grid">
        {/* Planner */}
        <section className="wr-card">
          <h3 className="wr-card-title">Planner</h3>
          <div className="wr-stats-row">
            <Stat value={review.plannerCreated} label="Created" />
            <Stat value={review.plannerDone}    label="Done" accent="green" />
            <Stat value={review.plannerSkipped} label="Skipped" accent="orange" />
            <Stat value={plannerRate + "%"}      label="Completion" accent="blue" />
          </div>
          {review.completedTitles?.length > 0 && (
            <ul className="wr-done-list">
              {review.completedTitles.map((t, i) => (
                <li key={i} className="wr-done-item">✓ {t}</li>
              ))}
            </ul>
          )}
        </section>

        {/* Habits */}
        <section className="wr-card">
          <h3 className="wr-card-title">Habits</h3>
          <div className="wr-stats-row">
            <Stat value={review.habitsActive}          label="Active" />
            <Stat value={review.habitCheckInsThisWeek} label="Check-ins" accent="green" />
            <Stat value={review.habitsPerfectWeek}     label="Perfect week" accent="blue" />
            <Stat value={habitRate + "%"}               label="Consistency" accent={habitRate >= 70 ? "green" : "orange"} />
          </div>
          {review.habitStats?.length > 0 && (
            <table className="wr-habit-table">
              <thead><tr><th>Habit</th><th>Days done</th></tr></thead>
              <tbody>
                {review.habitStats.map((h, i) => (
                  <tr key={i}>
                    <td>{h.name}</td>
                    <td><HeatBar done={h.completedDays} total={7} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Tasks */}
        <section className="wr-card">
          <h3 className="wr-card-title">Assigned Tasks</h3>
          <div className="wr-stats-row">
            <Stat value={review.tasksClosedThisWeek} label="Closed this week" accent="green" />
            <Stat value={review.tasksStillOpen}       label="Still open" accent={review.tasksStillOpen > 5 ? "red" : "blue"} />
          </div>
        </section>

        {/* Notes & Finance */}
        <section className="wr-card">
          <h3 className="wr-card-title">Notes & Finance</h3>
          <div className="wr-stats-row">
            <Stat value={review.notesCreatedThisWeek} label="Notes written" accent="blue" />
            {review.totalSpentThisWeek && (
              <Stat value={review.totalSpentThisWeek} label="Spent (month)" accent="orange" />
            )}
            {review.topSpendingCategory && (
              <Stat value={review.topSpendingCategory} label="Top category" />
            )}
          </div>
        </section>
      </div>

      {/* Reflection */}
      <section className="wr-reflection">
        <h3 className="wr-reflection-title">Reflect</h3>
        <div className="wr-reflection-grid">
          <ReflectionField
            label="✅ What went well this week?"
            value={reflection.wentWell}
            onChange={v => updateReflection("wentWell", v)}
            placeholder="Wins, progress, moments you're proud of…"
          />
          <ReflectionField
            label="⚡ What was challenging?"
            value={reflection.challenges}
            onChange={v => updateReflection("challenges", v)}
            placeholder="Obstacles, missed habits, hard moments…"
          />
          <ReflectionField
            label="🎯 What's my focus for next week?"
            value={reflection.nextFocus}
            onChange={v => updateReflection("nextFocus", v)}
            placeholder="One clear intention or goal…"
            fullWidth
          />
        </div>
        <p className="wr-reflection-saved">Saved locally to your device.</p>
      </section>
    </div>
  );
}

function ReflectionField({ label, value, onChange, placeholder, fullWidth }) {
  return (
    <div className={`wr-reflection-field ${fullWidth ? "wr-reflection-full" : ""}`}>
      <label className="wr-reflection-label">{label}</label>
      <textarea
        className="wr-reflection-textarea"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
      />
    </div>
  );
}

function Stat({ value, label, accent = "default" }) {
  return (
    <div className={`wr-stat wr-stat-${accent}`}>
      <div className="wr-stat-value">{value ?? "—"}</div>
      <div className="wr-stat-label">{label}</div>
    </div>
  );
}

function HeatBar({ done, total }) {
  return (
    <div className="wr-heat-bar">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`wr-heat-cell ${i < done ? "wr-heat-done" : ""}`} />
      ))}
    </div>
  );
}
